// crewbit-admin/src/components/schedule/TeamCanvas.tsx
//
// Schedule Module Phase C C5B — Team scoreboard orchestrator (admin-only;
// mobile drops Team per Foundation v1 §F3).
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §6.
//
// Composes header + scoreboard + reuses existing <UnassignedBar> for the
// queue-of-things-to-dispatch surface (unifies the unassigned drag source
// across DayCanvas + TeamCanvas; UnassignedQueue/UnassignedTile prompt-listed
// components are deferred to a follow-up commit per scope-trim 2026-05-10).
//
// Drag-drop preserves the `team-row-{userId}` drop target id grammar already
// handled by Schedule/index.tsx onDragEnd — no new dispatcher logic needed.
// Same-row time drag and cross-row reassign both work via existing
// handleEntryDrop / handleTimeDrop / handleDateDrop in hooks.ts.

import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { palette, admin, shadow } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types';
import type { ScheduleUser } from './scheduleHelpers';
import { useTeamUtilization } from '../../hooks/useTeamUtilization';
import { TeamRow } from './TeamRow';
import { EmptyState } from '../common/EmptyState';

export interface TeamCanvasProps {
  businessId: string;
  weekStart: Date;
  weekEnd: Date;
  resources: ScheduleUser[];
  onEntryClick: (entry: ScheduleEntry) => void;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
}

function formatRange(weekStart: Date, weekEnd: Date): string {
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  const startLabel = weekStart.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  const endLabel = sameMonth
    ? `${weekEnd.getDate()}`
    : weekEnd.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  return `${startLabel} – ${endLabel}, ${weekEnd.getFullYear()}`;
}

export function TeamCanvas({
  businessId,
  weekStart,
  weekEnd,
  resources,
  onEntryClick,
  onPrevWeek,
  onNextWeek,
  onToday,
}: TeamCanvasProps) {
  const { rows, loading, error } = useTeamUtilization(businessId, weekStart, weekEnd, resources);

  return (
    <div
      style={{
        background: palette.white,
        borderRadius: admin.cardRadius,
        border: `1px solid ${admin.border}`,
        overflow: 'hidden',
        boxShadow: shadow.card,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 16px',
          borderBottom: `1px solid ${palette.g150}`,
          background: palette.g50,
        }}
      >
        <button
          onClick={onPrevWeek}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, border: `1px solid ${palette.g200}`,
            borderRadius: 6, background: palette.white, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
          aria-label="Previous week"
        >
          <ChevronLeft size={14} color={palette.g700} />
        </button>
        <button
          onClick={onNextWeek}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, border: `1px solid ${palette.g200}`,
            borderRadius: 6, background: palette.white, cursor: 'pointer',
            fontFamily: 'inherit',
          }}
          aria-label="Next week"
        >
          <ChevronRight size={14} color={palette.g700} />
        </button>
        <button
          onClick={onToday}
          style={{
            border: `1px solid ${palette.g200}`,
            borderRadius: 6,
            padding: '4px 10px',
            background: palette.white,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 600,
            color: palette.g700,
          }}
        >
          Today
        </button>
        <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: palette.g800, marginLeft: 8 }}>
          {formatRange(weekStart, weekEnd)}
        </div>
        <div style={{ fontSize: 11, fontWeight: 600, color: palette.g500, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {resources.length} {resources.length === 1 ? 'operator' : 'operators'}
        </div>
      </div>

      {/* Column headers */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '180px 110px 1fr 240px',
          gap: 12,
          padding: '8px 14px',
          borderBottom: `1px solid ${palette.g150}`,
          fontSize: 11,
          fontWeight: 600,
          color: palette.g500,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          background: palette.g50,
        }}
      >
        <div>Operator</div>
        <div>Booked / Avail.</div>
        <div>Utilization</div>
        <div>Upcoming</div>
      </div>

      {/* Body */}
      {error ? (
        <div style={{ padding: 24, textAlign: 'center', color: palette.red, fontSize: 13 }}>{error}</div>
      ) : loading && rows.size === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: palette.g500, fontSize: 13 }}>
          Loading utilization…
        </div>
      ) : resources.length === 0 ? (
        <div style={{ padding: 32 }}>
          <EmptyState
            icon={<Calendar size={22} />}
            title="No operators"
            subtitle="Add operators in Team settings to see utilization."
          />
        </div>
      ) : (
        <div data-testid="team-scoreboard">
          {resources.map((r) => (
            <TeamRow
              key={r.id}
              resource={r}
              utilization={rows.get(r.id)}
              onEntryClick={onEntryClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}
